import { useState, useRef, useMemo, useEffect } from 'react';
import SearchBar from './components/SearchBar';
import ProductCard from './components/ProductCard';
import SourceFilter from './components/SourceFilter';
import SkeletonCard from './components/SkeletonCard';
import CoupangBanner from './components/CoupangBanner';
import { searchStream } from './api/search';
import './App.css';

const ALL_SOURCES = [
  { key: 'coupang', label: '쿠팡' },
  { key: 'naver', label: '네이버' },
  { key: '11st', label: '11번가' },
  { key: 'gmarket', label: '지마켓' },
];

const SORTS = [
  { key: 'price_asc', label: '낮은 가격순' },
  { key: 'price_desc', label: '높은 가격순' },
  { key: 'rating', label: '평점순' },
];

const SUGGESTIONS = [
  '무선마우스',
  '에어팟',
  '닌텐도 스위치',
  '갤럭시 버즈',
  '아이패드',
  '커피머신',
];

const PAGE_SIZE = 10; // 소스별 한 번에 노출할 개수

function getQueryFromUrl() {
  return new URLSearchParams(window.location.search).get('q') || '';
}

function App() {
  const [query, setQuery] = useState(() => getQueryFromUrl().trim());
  const [submitted, setSubmitted] = useState(() => !!getQueryFromUrl().trim());
  const [results, setResults] = useState([]);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState(null);
  const [activeSources, setActiveSources] = useState(
    ALL_SOURCES.map((s) => s.key)
  );
  const [sort, setSort] = useState('price_asc');
  const [perSourceLimit, setPerSourceLimit] = useState(PAGE_SIZE);
  const closeRef = useRef(null);

  useEffect(() => () => closeRef.current && closeRef.current(), []);

  // 검색 실행 (URL 갱신은 pushUrl 로 제어 — 뒤로가기/새로고침 동기화용)
  const runSearch = (q, { pushUrl = true } = {}) => {
    if (closeRef.current) closeRef.current();
    if (pushUrl) {
      window.history.pushState(
        { q },
        '',
        `${window.location.pathname}?q=${encodeURIComponent(q)}`
      );
    }

    setQuery(q);
    setSubmitted(true);
    setError(null);
    setResults([]);
    setPerSourceLimit(PAGE_SIZE);
    setPending(ALL_SOURCES.map((s) => s.key));

    closeRef.current = searchStream(q, {
      onStart: (info) => setPending(info?.sources || []),
      onResult: (r) => {
        setResults((prev) => [...prev, r]);
        setPending((prev) => prev.filter((s) => s !== r.source));
      },
      onDone: () => setPending([]),
      onError: () => {
        setError('연결이 끊겼습니다. 잠시 후 다시 시도해주세요.');
        setPending([]);
      },
    });
  };

  const handleSearch = (q) => runSearch(q);

  // URL 에 q 가 없는 상태(랜딩)로 초기화 — URL 은 건드리지 않음
  const resetState = () => {
    if (closeRef.current) closeRef.current();
    setQuery('');
    setSubmitted(false);
    setResults([]);
    setPending([]);
    setError(null);
    setPerSourceLimit(PAGE_SIZE);
  };

  const goHome = () => {
    window.history.pushState({}, '', window.location.pathname);
    resetState();
  };

  // 최초 진입/새로고침 시 URL 의 q 로 검색 복원 + 뒤로가기/앞으로가기 동기화
  useEffect(() => {
    const syncFromUrl = () => {
      const q = getQueryFromUrl().trim();
      if (q) runSearch(q, { pushUrl: false });
      else resetState();
    };
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAndSorted = useMemo(() => {
    const activeResults = results.filter((r) =>
      activeSources.includes(r.source)
    );
    const sortPage = (arr) => {
      const s = [...arr];
      if (sort === 'price_asc') {
        s.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
      } else if (sort === 'price_desc') {
        s.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));
      } else if (sort === 'rating') {
        s.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
      }
      return s;
    };

    // 페이지(소스별 10개)별로 정렬한 뒤, 페이지 순서대로 이어붙인다.
    // → 더보기로 추가된 항목은 기존 목록 사이가 아니라 항상 하단에 쌓인다.
    const pageCount = Math.ceil(perSourceLimit / PAGE_SIZE);
    const out = [];
    for (let p = 0; p < pageCount; p++) {
      const pageItems = activeResults.flatMap((r) =>
        (r.items || []).slice(p * PAGE_SIZE, (p + 1) * PAGE_SIZE)
      );
      out.push(...sortPage(pageItems));
    }
    return out;
  }, [results, activeSources, sort, perSourceLimit]);

  const countsBySource = useMemo(() => {
    const m = {};
    results.forEach(
      (r) => (m[r.source] = Math.min(r.items?.length || 0, perSourceLimit))
    );
    return m;
  }, [results, perSourceLimit]);

  // 활성 소스 중 아직 더 보여줄 항목이 있는지
  const hasMore = useMemo(
    () =>
      results.some(
        (r) =>
          activeSources.includes(r.source) &&
          (r.items?.length || 0) > perSourceLimit
      ),
    [results, activeSources, perSourceLimit]
  );

  const isLoading = pending.length > 0;
  const noResults =
    !isLoading && submitted && filteredAndSorted.length === 0 && !error;

  // === Landing ===
  if (!submitted) {
    return (
      <div className="app app--landing">
        <CoupangBanner />
        <main className="hero">
          <div className="hero__eyebrow">
            <span className="hero__eyebrow-dot" />
            4개 쇼핑몰 실시간 비교
          </div>
          <h1 className="hero__title">
            <span className="hero__title-accent">최저가</span>를
            <br />
            한 화면에서.
          </h1>
          <p className="hero__subtitle">
            쿠팡·네이버·11번가·지마켓 결과를 한 번의 검색으로 모아봅니다.
          </p>
          <div className="hero__search">
            <SearchBar
              onSearch={handleSearch}
              size="hero"
              autoFocus
              placeholder="찾고 싶은 상품을 검색해보세요"
            />
          </div>
          <div className="hero__chips">
            <div className="hero__chip-label">인기 검색어</div>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="hero__suggest"
                onClick={() => handleSearch(s)}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="hero__sources">
            <div className="hero__sources-label">Supported</div>
            <div className="hero__sources-list">
              {ALL_SOURCES.map((s) => (
                <span key={s.key} className="hero__source">
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        </main>

        <footer className="site-footer">
          쿠팡 상품의 경우 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의
          수수료를 제공받을 수 있습니다.
        </footer>
      </div>
    );
  }

  // === Search results ===
  return (
    <div className="app">
      <CoupangBanner />
      <header className="topbar">
        <div className="topbar__inner">
          <div
            className="brand"
            onClick={goHome}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && goHome()}
          >
            <span className="brand__dot" />
            searching
          </div>
          <SearchBar
            onSearch={handleSearch}
            loading={isLoading}
            initialValue={query}
            size="compact"
          />
        </div>
      </header>

      <main className="results">
        <div className="results__header">
          <h2 className="results__title">
            <span className="results__title-q">"{query}"</span> 검색 결과
          </h2>
          <div className="results__meta">
            총 {filteredAndSorted.length.toLocaleString('ko-KR')}건
            {isLoading && (
              <span className="results__pending">
                <span className="chip__spinner" />
                {pending
                  .map((s) => ALL_SOURCES.find((a) => a.key === s)?.label)
                  .join(' · ')}{' '}
                불러오는 중
              </span>
            )}
          </div>
        </div>

        <div className="toolbar">
          <SourceFilter
            sources={ALL_SOURCES}
            active={activeSources}
            counts={countsBySource}
            pending={pending}
            onChange={setActiveSources}
          />
          <div className="toolbar__sort">
            <label htmlFor="sort">정렬</label>
            <select
              id="sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {results
          .filter((r) => r.blocked && r.message)
          .map((r) => (
            <div className="notice" key={`${r.source}-notice`}>
              [{ALL_SOURCES.find((a) => a.key === r.source)?.label || r.source}]{' '}
              {r.message}
            </div>
          ))}

        {error && <div className="error">{error}</div>}

        <div className="product-grid">
          {filteredAndSorted.map((item, i) => (
            <ProductCard
              key={`${item.source}-${item.id || i}`}
              item={item}
              style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }}
            />
          ))}
          {isLoading &&
            filteredAndSorted.length < 12 &&
            Array.from({
              length: Math.max(8 - filteredAndSorted.length, 4),
            }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)}
        </div>

        {!isLoading && hasMore && (
          <div className="loadmore">
            <button
              type="button"
              className="loadmore__btn"
              onClick={() => setPerSourceLimit((n) => n + PAGE_SIZE)}
            >
              더보기
            </button>
          </div>
        )}

        {noResults && (
          <div className="empty">
            검색 결과가 없습니다. 다른 키워드로 시도해보세요.
          </div>
        )}
      </main>

      <footer className="site-footer">
        쿠팡 상품의 경우 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의
        수수료를 제공받을 수 있습니다.
      </footer>
    </div>
  );
}

export default App;
